import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import { GoogleTagManager, OneTrust, SettingsProvider, usePayOrGo } from '@vgn-medien-holding/vgn-fe-components';
import { restoreSsrCache, SessionProvider, UrqlProvider } from '@vgn-medien-holding/vgn-gql-shared-fe-queries';
import * as Fathom from 'fathom-client';
import type { AppProps } from 'next/app';
import App from 'next/app';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import type { Session } from 'next-auth';
import { useSession } from 'next-auth/react';
import 'overlayscrollbars/overlayscrollbars.css';
import { BrandColors } from '@components/atoms/BrandColors/BrandColors';
import { FavoritesSettingsHandler } from '@components/organisms/Authentication/FavoritesSettings/FavoritesSettingsHandler';
import { OverlayLoginFlow } from '@components/organisms/Authentication/OverlayLoginFlow/OverlayLoginFlow';
import { Empty } from '@components/templates/layouts/Empty';
import { PAY_OR_GO_SETTINGS } from '@src/constants/pay-or-go';
import type { Magazine, Navigation } from '@src/lib/graphql/generated';
import { settings } from '@src/settings/settings';
import packageInfos from 'package.json';
import { client, ssrCache, ssrQuery } from 'src/lib/graphql/urql';
import { FATHOM_ID } from 'tvm-config';
import '@src/styles/globals.css';
import AppContext, { defaultBrandingColors, type PowerBrandingColors } from '@lib/AppContext';
import { useScript } from '@lib/hooks/useScript';

const Toasts = dynamic(() => import('@components/molecules/Toasts/Toasts').then((mod) => mod.Toasts), {
  ssr: false,
});

const appVersion = packageInfos?.version;

const navHandles = {
  header: process.env.NEXT_PUBLIC_HEADER_NAV_HANDLE,
  footer: process.env.NEXT_PUBLIC_FOOTER_NAV_HANDLE,
};

const HIDE_ONETRUST_COOKIE_NAME = process.env.NEXT_PUBLIC_ONETRUST_HIDE_COOKIE_NAME || 'hideOneTrust';
const HIDE_ONETRUST_COOKIE_VALUE = process.env.NEXT_PUBLIC_ONETRUST_HIDE_COOKIE_VALUE || 'true';

const hasHideOneTrustCookie = (cookieHeader: string | undefined) =>
  (cookieHeader ?? '')
    .split(';')
    .some((c) => c.trim() === `${HIDE_ONETRUST_COOKIE_NAME}=${HIDE_ONETRUST_COOKIE_VALUE}`);

type PagePropsType = {
  navigations: { [key: string]: Navigation };
  magazines: Magazine[];
  errorStatus?: number;
  gtmId: string;
  otId: string;
  hideOneTrust?: boolean;
  session: Session;
};

type ComponentWithPageLayout = AppProps<PagePropsType> & {
  Component: AppProps['Component'] & {
    PageLayout?: React.ComponentType;
  };
};

function MyApp({ Component, pageProps }: ComponentWithPageLayout) {
  const { otId, gtmId, hideOneTrust } = pageProps;
  const [searchVisible, setSearchVisible] = useState(false);
  const Layout = Component.PageLayout || Empty;
  const [containerPolyfillShouldLoad, setContainerPolyfillShouldLoad] = useState(false);
  const [themeColors, setThemeColors] = useState<PowerBrandingColors>(defaultBrandingColors);

  const router = useRouter();
  // Account settings always use TV-MEDIA colors, including after client navigation
  // from a page whose Power Branding is still stored in the app context.
  const activeThemeColors = router.pathname === '/settings' ? defaultBrandingColors : themeColors;
  restoreSsrCache({ pageProps, ssrCache });

  useEffect(() => {
    console.info(
      `%cTV-Media v${appVersion}`,
      `width: 100vw; display: block; background: linear-gradient(to bottom right, #FF0000, #550000); color: #fff; padding: 0.5rem 1.5rem ; margin: 1.5rem 2.5rem 2.5rem 0; border-radius: 3rem;`,
    );
    setContainerPolyfillShouldLoad(!('container' in document.documentElement.style));

    Fathom.load(FATHOM_ID, {});

    function onRouteChangeComplete() {
      Fathom.trackPageview();
    }
    router.events.on('routeChangeComplete', onRouteChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', onRouteChangeComplete);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.events]);

  useScript(`https://cdn.jsdelivr.net/npm/container-query-polyfill@1/dist/container-query-polyfill.modern.js`, {
    removeOnUnmount: false,
    shouldPreventLoad: !containerPolyfillShouldLoad,
  });

  const providerValues = useMemo(
    () => ({
      header: {
        navigation: pageProps.navigations?.header,
      },
      search: {
        searchVisible: searchVisible,
        setSearchVisible: setSearchVisible,
      },
      footer: {
        navigation: pageProps.navigations?.footer,
      },
      magazines: pageProps.magazines,
      theme: {
        colors: activeThemeColors,
        setColors: setThemeColors,
      },
    }),
    [
      pageProps.magazines,
      pageProps.navigations?.footer,
      pageProps.navigations?.header,
      searchVisible,
      activeThemeColors,
    ],
  );

  return (
    <SessionProvider session={pageProps.session}>
      <AppContext.Provider value={providerValues}>
        <BrandColors colors={activeThemeColors} />
        <SettingsProvider settings={settings}>
          <UrqlProvider value={client}>
            <FavoritesSettingsHandler />
            <Layout>
              {otId && !hideOneTrust && (
                <OneTrust
                  autoblock={false}
                  otId={otId}
                  payOrGoId={PAY_OR_GO_SETTINGS.id}
                  aboButtonClassName="!bg-black"
                />
              )}
              {gtmId && <GoogleTagManager gtmId={gtmId} customSource={'https://data.tv-media.at/eeyeitdp.js?st='} />}
              <Component {...pageProps} statusCode={pageProps.errorStatus} />
              <OverlayLoginFlow />
              <PayOrGoConsent />
              <Toasts />
            </Layout>
          </UrqlProvider>
        </SettingsProvider>
      </AppContext.Provider>
    </SessionProvider>
  );
}

MyApp.getInitialProps = async (appContext) => {
  const { res, req } = appContext.ctx;
  const cookieHeader = req?.headers?.cookie ?? (typeof document !== 'undefined' ? document.cookie : '');
  const appProps = await App.getInitialProps(appContext);
  if (appProps.pageProps?.errorStatus && res) {
    res.statusCode = appProps.pageProps.errorStatus;
  }

  const { GetMagazinesDocument, GetNavigationByHandlesDocument } = await import('@src/lib/graphql/generated');

  const handles = Object.values(navHandles).filter((i) => i) as string[];
  const { data: navData } = await ssrQuery({
    query: GetNavigationByHandlesDocument,
    variables: { handles },
  });

  const navigations = Object.entries(navHandles).map(([key, value]) => ({
    [key]: navData?.navigationByHandles
      ? navData?.navigationByHandles.find((nav) => nav?.handle === value) || null
      : null,
  }));

  const { data: magazinesData } = await ssrQuery({
    query: GetMagazinesDocument,
    variables: {
      context: 'tvmedia',
    },
  });

  return {
    pageProps: {
      navigations: Object.assign({}, ...navigations),
      errorStatus: res?.statusCode,
      magazines: magazinesData?.magazines,
      gtmId: process.env.NEXT_PUBLIC_GTM_ID || null,
      otId: process.env.NEXT_PUBLIC_ONETRUST_ID || null,
      hideOneTrust: hasHideOneTrustCookie(cookieHeader),
    },
  };
};

export default MyApp;

const PayOrGoConsent = () => {
  const { data: session } = useSession() ?? {};
  const permissions = (session?.user as { permissions?: string[] } | undefined)?.permissions;
  usePayOrGo({
    hasPermission: permissions?.includes(PAY_OR_GO_SETTINGS.permission),
    alertMessage: PAY_OR_GO_SETTINGS.alertMessage,
  });
  return null;
};
